import { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { adminServiceAiBaseUrl } from './utils'

const parseApiError = async (res) => {
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    const errBody = await res.json().catch(() => ({}))
    throw new Error(errBody?.message || `HTTP ${res.status}`)
  }

  const text = await res.text()
  const trimmed = text.trim()
  if (trimmed.startsWith('{')) {
    try {
      const errBody = JSON.parse(trimmed)
      if (errBody?.message) {
        throw new Error(errBody.message)
      }
    } catch (err) {
      if (err instanceof Error && err.message !== 'Unexpected token') {
        throw err
      }
    }
  }

  if (!res.ok) {
    throw new Error(trimmed || `HTTP ${res.status}`)
  }

  return trimmed
}

export const useAiChatStream = (buildingId) => {
  const token = useSelector((state) => state.auth?.token)
  const [isStreaming, setIsStreaming] = useState(false)
  const abortRef = useRef(null)

  const sendMessage = useCallback(
    async (messages, onToken, onDone, onError) => {
      if (!buildingId || !token) {
        onError?.(new Error('not authenticated'))
        return
      }

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setIsStreaming(true)

      try {
        const res = await fetch(
          `${adminServiceAiBaseUrl}api/v2/buildings/${buildingId}/ai/chat`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ messages }),
            signal: controller.signal,
          }
        )

        const contentType = res.headers.get('content-type') || ''
        if (!res.ok || contentType.includes('application/json')) {
          await parseApiError(res)
        }

        const reader = res.body?.getReader()
        if (!reader) {
          throw new Error('stream not supported')
        }

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const parts = buffer.split('\n\n')
          buffer = parts.pop() || ''

          for (const part of parts) {
            const lines = part.split('\n')
            let event = 'message'
            let dataLine = ''
            for (const line of lines) {
              if (line.startsWith('event:')) {
                event = line.slice(6).trim()
              } else if (line.startsWith('data:')) {
                dataLine = line.slice(5).trim()
              }
            }
            if (event === 'done') {
              onDone?.()
              continue
            }
            if (!dataLine) continue
            try {
              const parsed = JSON.parse(dataLine)
              if (parsed?.content) {
                onToken?.(parsed.content)
              } else if (parsed?.message) {
                throw new Error(parsed.message)
              }
            } catch (err) {
              if (err instanceof Error && err.message && !err.message.startsWith('Unexpected')) {
                throw err
              }
              onToken?.(dataLine)
            }
          }
        }
        onDone?.()
      } catch (err) {
        if (err?.name !== 'AbortError') {
          onError?.(err)
        }
      } finally {
        setIsStreaming(false)
      }
    },
    [buildingId, token]
  )

  const abort = useCallback(() => {
    abortRef.current?.abort()
    setIsStreaming(false)
  }, [])

  return { sendMessage, isStreaming, abort }
}
